import { useState, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useInterviewStore } from '@/store/interviewStore'
import { useInterview } from '@/hooks/useInterview'
import { InterviewSessionContext } from '@/context/InterviewSessionContext'
import type { Language, AvatarGender, InterviewConfig } from '@/types/interview'

async function loadConfig(lang: Language): Promise<Partial<InterviewConfig>> {
  try {
    const res = await fetch(`/static/candidate_interview_config_${lang === 'de' ? 'de' : 'en'}.json`)
    const data = await res.json()
    // question1/question2 deliberately NOT read from this static file —
    // questions come from deployment_config (DEPLOYMENT_FALLBACK_QUESTIONS_*) via
    // useDeploymentConfig() consumed in useInterview.getScript.
    return {
      mode: data.mode ?? (data.questions?.length === 1 ? 'demo' : 'full'),
    }
  } catch {
    return { mode: 'demo' }
  }
}

export function CandidateLayout() {
  const [language, setLanguage] = useState<Language>('de')
  const [loadedConfig, setLoadedConfig] = useState<Partial<InterviewConfig>>({})

  const navigate = useNavigate()
  const store = useInterviewStore()
  const interview = useInterview()

  // When interview completes naturally, go to summary
  useEffect(() => {
    if (store.status === 'complete') {
      navigate('thankyou', { replace: true })
    }
  }, [store.status, navigate])

  function handleSelectLanguage(lang: Language) {
    setLanguage(lang)
    loadConfig(lang).then(setLoadedConfig)
    navigate('avatar')
  }

  function handleSelectAvatar(gender: AvatarGender) {
    navigate('instructions')
    const config: InterviewConfig = {
      language,
      avatarGender: gender,
      mode: loadedConfig.mode ?? 'demo',
      ...loadedConfig,
    }
    interview.setup(config).catch(console.error)
  }

  function handleContinue() {
    navigate('interview')
  }

  async function handleStart() {
    try {
      await interview.startInterview()
    } catch (err) {
      console.error('Start failed:', err)
    }
  }

  function handleNext() {
    interview.advanceSegment()
  }

  function handleEnd() {
    // Not running yet: nothing to finalize server-side, just leave.
    if (store.status !== 'running') {
      window.location.href = '/'
      return
    }
    // Manual end of a running interview is an abort: finalize/close the
    // session properly (stops egress, tears the worker down) instead of a
    // hard redirect that left the server-side recording running until the
    // room-empty timeout. The status→'complete' effect above then routes
    // to the thank-you screen, which shows the aborted variant.
    void interview.endInterview(true)
  }

  function handleBackToHome() {
    window.location.href = 'https://www.tlou.ai'
  }

  function handleBackToLanguage() {
    navigate('language')
  }

  function handleBackToAvatar() {
    navigate('avatar')
  }

  return (
    <InterviewSessionContext.Provider
      value={{
        language,
        loadedConfig,
        interview,
        handleSelectLanguage,
        handleSelectAvatar,
        handleContinue,
        handleStart,
        handleNext,
        handleEnd,
        handleBackToHome,
        handleBackToLanguage,
        handleBackToAvatar,
      }}
    >
      <Outlet />
    </InterviewSessionContext.Provider>
  )
}